/**
 * AI sobre el board: modos Prompt | Foto | Sketch e inyección del resultado.
 */

import {
  buildSketchPrompt,
  sketchViewsForChoice,
} from "../AI/nuevoDisenoSketchPrompt";

export const BOARD_AI_MODES = Object.freeze(["prompt", "photo", "sketch"]);

export function normalizeBoardAiMode(raw, { fallback = "prompt" } = {}) {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "prompt" || v === "generate" || v === "text") return "prompt";
  if (v === "photo" || v === "foto" || v === "image") return "photo";
  if (v === "sketch" || v === "sketches" || v === "dibujo") return "sketch";
  const fb = String(fallback || "prompt")
    .trim()
    .toLowerCase();
  if (fb === "photo" || fb === "sketch") return fb;
  return "prompt";
}

/** Prompt efectivo según modo (sketch ignora el texto libre del usuario). */
export function resolveBoardAiPrompt({
  mode,
  prompt = "",
  sketchView = "front",
} = {}) {
  const m = normalizeBoardAiMode(mode);
  if (m === "sketch") {
    const view = sketchViewsForChoice(sketchView)[0] || "front";
    return buildSketchPrompt(view);
  }
  return String(prompt || "").trim();
}

export function boardAiCanSubmit({
  mode,
  prompt = "",
  engine = "gpt-image",
  refCount = 0,
} = {}) {
  const m = normalizeBoardAiMode(mode);
  if (m === "sketch") return Number(refCount) > 0;
  const p = String(prompt || "").trim();
  if (p.length < 3) return false;
  if (m === "prompt" && engine === "flux" && Number(refCount) <= 0)
    return false;
  return true;
}

/**
 * Nombre de archivo seguro para inyectar el resultado de createNuevoDiseno.
 */
export function boardAiResultFilename(resp = {}, { mode = "photo" } = {}) {
  const m = normalizeBoardAiMode(mode);
  const fromApi = String(resp.filename || resp.pngFilename || "")
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "-");
  if (fromApi) return fromApi.slice(0, 180);
  const stamp = Date.now();
  if (m === "sketch") return `sketch_${stamp}.png`;
  if (m === "photo") return `photo_${stamp}.png`;
  return `ai_${stamp}.png`;
}

/** Ruta relativa API del resultado (png preferido). */
export function boardAiResultRelativePath(resp = {}) {
  const path = String(resp.pngRelativeUrl || resp.relativeUrl || "").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Descarga el resultado de /api/designs/create como File listo para addAssets.
 */
export async function fileFromDesignCreateResponse(
  resp,
  { mode = "photo", apiUrl = "", fetchImpl = fetch } = {},
) {
  const path = boardAiResultRelativePath(resp);
  if (!path) return null;
  let url = path;
  if (!/^https?:\/\//i.test(path) && !path.startsWith("data:")) {
    const base = String(apiUrl || "").replace(/\/$/, "");
    url = `${base}/api${path.startsWith("/") ? path : `/${path}`}`;
  }
  const response = await fetchImpl(url);
  if (!response?.ok) return null;
  const blob = await response.blob();
  if (!blob?.size) return null;
  return new File([blob], boardAiResultFilename(resp, { mode }), {
    type: blob.type || "image/png",
  });
}

/**
 * Place target for handleUpload after studio generate.
 * @returns {{ placeAt: object|null, pasteboardAt: object|null, poolOnly: boolean }}
 */
export function boardAiPlaceOptions(place = "pasteboard") {
  const p = String(place || "pasteboard")
    .trim()
    .toLowerCase();
  if (p === "page") {
    return { placeAt: { x: 0.5, y: 0.5 }, pasteboardAt: null, poolOnly: false };
  }
  if (p === "pool") {
    return { placeAt: null, pasteboardAt: null, poolOnly: true };
  }
  return {
    placeAt: null,
    pasteboardAt: { x: 0.12, y: 0.12 },
    poolOnly: false,
  };
}
